// Play — the whole of Nazareth: exploring, gathering, crafting, helping,
// caring for the Holy Family, praying, and the gentle day cycle. One compact
// interconnected world; overlays (crafting book, dialog, codex, mini-games)
// pause it. Pure logic lives in state.ts; this scene is the living surface.
import { Scene, type Draw, type BitmapFont, type Preload } from '../../engine/webgpu.js';
import {
  C, CHARSET, FONT_SERIF, MOVE_SPEED, INTERACT_RANGE, DAY_LENGTH, COST,
  STRENGTH_MAX, PEACE_MAX, QUALITY_NAMES, HUD, INTERIOR, PANEL, WORKBENCH_COST, ANIMAL, REGION,} from '../config.js';
import { state } from '../session.js';
import { qualityFromScore } from '../state.js';
import {
  recipeById, recipesFor, requestById, MATERIALS, CODEX, PRAYERS,
  COMMUNITY_PROJECT, CHAPTERS, type Recipe, type MaterialKey, type CodexEntry,
} from '../data.js';
import {
  WORLD_W, WORLD_H, STRUCTS, NODES, NPCS, ANIMALS, WORK_ANIMALS, SPOTS, PRAYER_SPOTS, SCROLLS, regionAt,
  type NodeDef, type NpcDef, type AnimalId, type WorkAnimalDef,
  type AnimalDef,
} from '../world.js';
import * as R from '../render.js';
import { bakeWorldTextures } from '../assets/textures.js';
import * as INT from '../assets/interior.js';
import {
  INTERIORS, DOORS, interiorById, inRect,
  type Furniture, type Interior, type VenueId,
} from '../interiors.js';
import { play as sfx } from '../audio.js';

type Facing = R.Facing;
type Overlay = 'none' | 'daystart' | 'book' | 'minigame' | 'chop' | 'inventory'
  | 'dialog' | 'codex' | 'prayer' | 'chapter' | 'ending';

interface DlgOption { label: string; act: () => void; }
interface Dialog { title: string; body: string[]; options: DlgOption[]; sel: number; }

interface Target { type: string; x: number; y: number; prompt: string; ref?: unknown; }

export class Play extends Scene {
  private fBig!: BitmapFont; private fH1!: BitmapFont; private fH2!: BitmapFont;
  private fBody!: BitmapFont; private fSmall!: BitmapFont;

  private st = state;
  private px = SPOTS.workbench.x; private py = SPOTS.workbench.y + 60;
  private facing: Facing = 'down';
  private moving = false; private walkPhase = 0;
  private actionT = 0; private actionName = '';
  private tools = ['Axe', 'Saw', 'Hammer', 'Measure']; private tool = 0;

  private dayT = 0.06; private t = 0;
  private tufts: { x: number; y: number; k: number }[] = [];
  private npcPos: Record<string, {
    x: number; y: number;
    facing: R.Facing; walk: number; moving: boolean;
  }> = {};
  private jesusInShop = false; private jesusPos = { x: 800, y: 1210 };

  private overlay: Overlay = 'daystart';
  private dialog: Dialog | null = null;
  private bookSel = 0;
  private codexCat = 0; private codexSel = 0; private codexReading = false;
  private prayerText = '';
  private chapterShown = 1; private chapterTtl = 0;
  private target: Target | null = null;
  private collected = new Set<string>();
  private toasts: { text: string; ttl: number }[] = [];
  private endT = 0;

  // chop mini-game
  private chop = { node: null as NodeDef | null, pos: 0, dir: 1, done: false, resultT: 0, msg: '' };
  // craft mini-game
  private mg = { recipe: null as Recipe | null, step: 0, scores: [] as number[], pos: 0, off: 0, resultT: 0, quality: 0 };

  // touch helpers
  private hudW = 1280; private hudH = 768;
  private view = { x: 0, y: 0, w: 1280, h: 768 };
  private moveTarget: { x: number; y: number } | null = null;

  // ── venue: where Joseph actually is. Interiors are a sub-state of Play, not
  // a scene — a scene transition would reset the clock to dawn, teleport every
  // villager and respawn every chopped tree (see the plan). They are also not
  // an overlay: an overlay freezes the world, and a room is a place, not a menu.
  private venue: 'outside' | VenueId = 'outside';
  /** Seconds left of the warm dip across a threshold. Ticked in update(), ABOVE
   *  the overlay switch, so it always completes even if a dialog opens on
   *  arrival. */
  private dipT = 0;
  private dipIn = true;
  /** The door latch. A door only fires once Joseph has stepped clear of it, so
   *  he can never bounce in-out-in. A latch, not a timer: a timer ticked in
   *  updateWorld would freeze under the very dialog the doorway opens. */
  private doorArmed = true;
  /** workshopLevel() can change mid-visit when a craft crosses a threshold;
   *  snapshot it so furniture never appears on top of Joseph. */
  private shopLevel = 1;
  /** Display safe-area insets in CSS px. Non-zero only where the screen eats
   *  its own edges — a notch, rounded corners, a home indicator. The canvas is
   *  full-bleed by design, so the HUD has to keep inside these or it is drawn
   *  where the player cannot see it. */
  private safe = { top: 0, right: 0, bottom: 0, left: 0 };

  /** The working animals. `out` means it is following him; otherwise it stands
   *  at the byre. Only ONE is ever out — taking the other swaps them. */
  private beast: Record<AnimalId, {
    x: number; y: number; facing: R.Facing; walk: number;
    moving: boolean; out: boolean; stuck: number; loadT: number;
  }> = {
    donkey: { x: 0, y: 0, facing: 'down', walk: 0, moving: false, out: false, stuck: 0, loadT: 0 },
    ox: { x: 0, y: 0, facing: 'down', walk: 0, moving: false, out: false, stuck: 0, loadT: 0 },
  };
  /** Joseph's recent positions. An animal walks THIS, not a straight line to
   *  him — every crumb is walkable because he stood on it, so it can never be
   *  steered into a wall. */
  private trail: Array<{ x: number; y: number }> = [];
  private trailT = 0;
  /** Which way he is actually travelling — doorways only open to someone
   *  heading into them, never to someone walking past or stepping away. */
  private vx = 0; private vy = 0;
  private touchBtns: { id: string; x: number; y: number; w: number; h: number; label: string }[] = [];

  override preload(_load: Preload): void { /* all art is procedural */ }

  override setup(data?: unknown): void {
    const d = (data ?? {}) as { fresh?: boolean };
    this.st = state;
    this.fBig = this.game.assets.font({ font: `bold 46px ${FONT_SERIF}`, charset: CHARSET });
    this.fH1 = this.game.assets.font({ font: `bold 30px ${FONT_SERIF}`, charset: CHARSET });
    this.fH2 = this.game.assets.font({ font: `bold 22px ${FONT_SERIF}`, charset: CHARSET });
    this.fBody = this.game.assets.font({ font: `19px ${FONT_SERIF}`, charset: CHARSET });
    this.fSmall = this.game.assets.font({ font: `15px ${FONT_SERIF}`, charset: CHARSET });
    // Bake the grain / mottle / dapple sheets once and hand them to render.ts,
    // which uses them for every wooden, stone and leafy surface in the world.
    R.useTextures(bakeWorldTextures(this.game));

    this.st.refreshOffered();
    if (this.st.freePlay) this.reofferAll();
    this.buildTufts();
    for (const n of NPCS) this.npcPos[n.id] = { ...n.work, facing: 'down', walk: 0, moving: false };
    for (const a of WORK_ANIMALS) this.stableAnimal(a);
    this.newDaySetup(d.fresh ?? false);

    this.camera.bounds = { x: 0, y: 0, w: WORLD_W, h: WORLD_H };
    this.px = SPOTS.workbench.x + 20; this.py = SPOTS.workbench.y + 150; this.facing = 'down';

    this.readSafeArea();
    addEventListener('resize', this.onResize);
    addEventListener('orientationchange', this.onResize);
    this.input.bind({
      up: ['ArrowUp', 'KeyW'], down: ['ArrowDown', 'KeyS'],
      left: ['ArrowLeft', 'KeyA'], right: ['ArrowRight', 'KeyD'],
      interact: ['KeyX'], tool: ['Space'], book: ['Tab'],
      inv: ['KeyI'], codex: ['KeyC'], prev: ['KeyQ'], next: ['KeyE'],
      back: ['Escape'],
    });
    this.input.onTap((e) => this.onTap(e.x, e.y));
    this.input.onSwipe((e) => this.onSwipe(e.direction));
  }

  private onResize = (): void => { this.readSafeArea(); };

  private readSafeArea(): void {
    try {
      const cs = getComputedStyle(document.documentElement);
      const px = (name: string): number => Math.max(0, Math.round(parseFloat(cs.getPropertyValue(name)) || 0));
      this.safe = {
        top: px('--sai-top'), right: px('--sai-right'),
        bottom: px('--sai-bottom'), left: px('--sai-left'),
      };
    } catch { this.safe = { top: 0, right: 0, bottom: 0, left: 0 }; }
  }

  /** The rectangle the HUD may actually use: the frame minus anything the
   *  display itself covers. Every panel is laid out against THIS, not d.w/d.h. */
  private hudRect(d: Draw): { x: number; y: number; w: number; h: number } {
    return {
      x: this.safe.left,
      y: this.safe.top,
      w: Math.max(320, d.w - this.safe.left - this.safe.right),
      h: Math.max(240, d.h - this.safe.top - this.safe.bottom),
    };
  }

  private reofferAll(): void {
    for (const id in this.st.requests) if (this.st.requests[id] === 'done') this.st.requests[id] = 'offered';
  }

  private buildTufts(): void {
    let s = 1234567;
    const rnd = () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
    for (let i = 0; i < 520; i++) {
      this.tufts.push({ x: rnd() * WORLD_W, y: 120 + rnd() * (WORLD_H - 160), k: rnd() });
    }
  }

  private newDaySetup(_fresh: boolean): void {
    this.jesusInShop = ((this.st.day + 1) % 2 === 0);
    this.jesusPos = this.jesusInShop
      ? { x: SPOTS.workbench.x + 90, y: SPOTS.workbench.y + 30 }
      : { x: SPOTS.jesusHome.x, y: SPOTS.jesusHome.y };
    this.overlay = 'daystart';
  }

  // ── update ──────────────────────────────────────────────────────────────────
  override update(dt: number): void {
    super.update(dt);
    this.t += dt;
    // camera always rests on Joseph (even while an overlay is up)
    this.camera.x = this.px - this.width / 2;
    this.camera.y = this.py - this.height / 2;
    if (this.actionT > 0) this.actionT -= dt;
    if (this.dipT > 0) this.dipT = Math.max(0, this.dipT - dt);
    for (let i = this.toasts.length - 1; i >= 0; i--) { this.toasts[i].ttl -= dt; if (this.toasts[i].ttl <= 0) this.toasts.splice(i, 1); }

    switch (this.overlay) {
      case 'none': this.updateWorld(dt); break;
      case 'daystart': if (this.confirmPressed()) { this.overlay = 'none'; sfx(this, 'chime'); } break;
      case 'book': this.updateBook(); break;
      case 'inventory': if (this.input.keys.book.pressed || this.input.keys.interact.pressed || this.input.keys.inv.pressed || this.input.keys.back.pressed) this.overlay = 'none'; break;
      case 'dialog': this.updateDialog(); break;
      case 'codex': this.updateCodex(); break;
      case 'chop': this.updateChop(dt); break;
      case 'minigame': this.updateMinigame(dt); break;
      case 'prayer': this.updatePrayer(); break;
      case 'chapter': this.chapterTtl -= dt; if (this.confirmPressed() || this.chapterTtl <= 0) { this.overlay = 'none'; if (this.chapterShown === 5) this.revealCommunity(); } break;
      case 'ending': this.endT += dt; break;
    }
  }

  private confirmPressed(): boolean {
    return this.input.keys.interact.pressed || this.input.keys.tool.pressed || this.input.keys.book.pressed;
  }

  private updateWorld(dt: number): void {
    const k = this.input.keys;
    let vx = 0, vy = 0;
    if (k.left.held) vx -= 1; if (k.right.held) vx += 1;
    if (k.up.held) vy -= 1; if (k.down.held) vy += 1;
    if (vx || vy) this.moveTarget = null;
    if (!vx && !vy && this.moveTarget) {
      const dx = this.moveTarget.x - this.px, dy = this.moveTarget.y - this.py;
      const dist = Math.hypot(dx, dy);
      if (dist < 8) this.moveTarget = null; else { vx = dx / dist; vy = dy / dist; }
    }
    this.moving = !!(vx || vy);
    if (this.moving) {
      const len = Math.hypot(vx, vy) || 1; vx /= len; vy /= len;
      this.vx = vx; this.vy = vy;
      const spd = MOVE_SPEED * this.st.efficiency() * dt;
      const nx = this.px + vx * spd, ny = this.py + vy * spd;
      if (!this.blocked(nx, this.py)) this.px = nx;
      if (!this.blocked(this.px, ny)) this.py = ny;
      const room = this.interior();
      if (room) {
        const W = INTERIOR.wall + 10;
        this.px = Math.max(room.room.x + W, Math.min(room.room.x + room.room.w - W, this.px));
        this.py = Math.max(room.room.y + W + 10, Math.min(room.room.y + room.room.h - W, this.py));
      } else {
        this.px = Math.max(40, Math.min(WORLD_W - 40, this.px));
        this.py = Math.max(140, Math.min(WORLD_H - 30, this.py));
      }
      if (Math.abs(vx) > Math.abs(vy)) this.facing = vx < 0 ? 'left' : 'right';
      else this.facing = vy < 0 ? 'up' : 'down';
      this.walkPhase += dt * 10;
    }
    // breadcrumbs, then the animals that walk them
    this.trailT += dt;
    if (this.trailT >= ANIMAL.trailStep) {
      this.trailT = 0;
      this.trail.push({ x: this.px, y: this.py });
      const keep = Math.ceil(ANIMAL.trailLag / ANIMAL.trailStep) + 6;
      while (this.trail.length > keep) this.trail.shift();
    }
    this.updateBeasts(dt);
    this.checkDoors();

    // day cycle advances gently in the world
    this.dayT = Math.min(1, this.dayT + dt / DAY_LENGTH);
    // working slowly lowers peace; being idle/at home nudges it up
    if (this.actionT > 0) this.st.addPeace(-dt * 0.6);

    // Villagers keep one small routine: their own station through the day, home
    // in the evening, and a slow drift around whichever it is. Deliberately not
    // a simulation — they walk there, then potter about, and that is all.
    const evening = this.dayT > 0.72;
    for (let i = 0; i < NPCS.length; i++) {
      const n = NPCS[i];
      const p = this.npcPos[n.id];
      const anchor = evening ? n.home : n.work;
      // a gentle idle orbit so nobody stands frozen on their mark
      const ph = i * 2.4;
      const tx = anchor.x + Math.cos(this.t * 0.16 + ph) * n.wander;
      const ty = anchor.y + Math.sin(this.t * 0.11 + ph * 1.7) * n.wander * 0.6;
      const dx = tx - p.x, dy = ty - p.y;
      const dist = Math.hypot(dx, dy);
      p.moving = dist > 3;
      if (p.moving) {
        const spd = Math.min(dist, (n.id === 'child_ruth' ? 78 : 52) * dt);
        p.x += (dx / dist) * spd;
        p.y += (dy / dist) * spd;
        p.walk += dt * 7;
        p.facing = Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
      } else {
        p.walk = 0;
      }
    }

    this.target = this.resolveTarget();
    if (k.interact.pressed && this.target) this.doInteract(this.target);
    if (k.tool.pressed && this.target && this.target.type === 'node') this.doInteract(this.target);
    if (k.book.pressed) this.openInventory();
    if (k.inv.pressed) this.openInventory();
    if (k.codex.pressed) this.openCodex();
    if (k.next.pressed) { this.tool = (this.tool + 1) % this.tools.length; sfx(this, 'select'); }
    if (k.prev.pressed) { this.tool = (this.tool + this.tools.length - 1) % this.tools.length; sfx(this, 'select'); }

    // progression
    const adv = this.st.advanceChapterIfReady();
    if (adv) { this.chapterShown = adv; this.chapterTtl = 6; this.overlay = 'chapter'; sfx(this, 'chapter'); }
    if (this.st.chapter === 5 && this.st.communityComplete() && this.endT === 0) this.startEnding();
  }

  private blocked(x: number, y: number): boolean {
    const room = this.interior();
    if (room) {
      for (const f of room.furniture) {
        if (!f.solid) continue;
        if (f.minLevel && this.shopLevel < f.minLevel) continue;
        if (f.requires && !this.st.requestDone(f.requires)) continue;
        if (x > f.x - 10 && x < f.x + f.w + 10 && y > f.y + f.h * 0.35 && y < f.y + f.h + 8) return true;
      }
      return false;
    }
    for (const s of STRUCTS) {
      if (!s.solid) continue;
      const fx = s.x + s.w * 0.08, fy = s.y + s.h * 0.5, fw = s.w * 0.84, fh = s.h * 0.5;
      if (x > fx - 14 && x < fx + fw + 14 && y > fy - 6 && y < fy + fh + 6) return true;
    }
    return false;
  }

  // ── the working animals ──────────────────────────────────────────────────
  /** Put an animal back in its place at the byre. */
  private stableAnimal(a: WorkAnimalDef): void {
    const s = this.beast[a.id];
    s.x = a.home.x; s.y = a.home.y;
    s.out = false; s.moving = false; s.walk = 0; s.stuck = 0;
    s.facing = 'down';
  }

  /** Give up on the trail and appear just behind him. The fallback that makes
   *  this robust: he is always standing somewhere walkable. */
  private snapBeast(id: AnimalId): void {
    const s = this.beast[id];
    const back: Record<R.Facing, [number, number]> = {
      up: [0, 1], down: [0, -1], left: [1, 0], right: [-1, 0],
    };
    const [bx, by] = back[this.facing];
    s.x = this.px + bx * ANIMAL.followGap;
    s.y = this.py + by * ANIMAL.followGap;
    s.stuck = 0;
  }

  private updateBeasts(dt: number): void {
    const lag = Math.ceil(ANIMAL.trailLag / ANIMAL.trailStep);
    for (const a of WORK_ANIMALS) {
      const s = this.beast[a.id];
      if (s.loadT > 0) s.loadT = Math.max(0, s.loadT - dt);
      // not out, or he has gone indoors: it stands where it is and waits
      if (!s.out || this.venue !== 'outside') { s.moving = false; s.walk = 0; continue; }

      const far = Math.hypot(this.px - s.x, this.py - s.y);
      if (far > ANIMAL.snapDist) { this.snapBeast(a.id); continue; }

      const crumb = this.trail[Math.max(0, this.trail.length - 1 - lag)]
        ?? { x: this.px, y: this.py };
      const dx = crumb.x - s.x, dy = crumb.y - s.y;
      const dist = Math.hypot(dx, dy);
      s.moving = dist > ANIMAL.followGap * 0.4;
      if (!s.moving) { s.walk = 0; s.stuck = 0; continue; }

      const step = Math.min(dist, ANIMAL.speed * dt);
      s.x += (dx / dist) * step;
      s.y += (dy / dist) * step;
      s.walk += dt * 6;
      s.facing = Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
      // if it is trying to move but getting nowhere, stop being clever
      s.stuck = step < 0.35 ? s.stuck + dt : 0;
      if (s.stuck > ANIMAL.stuckTime) this.snapBeast(a.id);
    }
  }

  /** The one animal currently following him, if any. */
  private beastOut(): AnimalId | null {
    for (const a of WORK_ANIMALS) if (this.beast[a.id].out) return a.id;
    return null;
  }

  private toggleBeast(a: WorkAnimalDef): void {
    const s = this.beast[a.id];
    if (s.out) {
      this.stableAnimal(a);
      this.toast(`You send ${a.name} back to the byre.`);
      sfx(this, 'select');
      return;
    }
    // only one at a time — taking this one sends the other home rather than
    // refusing, so the interaction never dead-ends
    const other = this.beastOut();
    if (other) {
      const o = WORK_ANIMALS.find((w) => w.id === other)!;
      this.stableAnimal(o);
      this.toast(`${o.name[0].toUpperCase()}${o.name.slice(1)} goes home; ${a.name} follows.`);
    } else {
      this.toast(`${a.name[0].toUpperCase()}${a.name.slice(1)} follows you.`);
    }
    s.out = true;
    this.snapBeast(a.id);
    sfx(this, 'chime');
  }

  // ── doorways ─────────────────────────────────────────────────────────────
  private interior(): Interior | undefined {
    return this.venue === 'outside' ? undefined : interiorById(this.venue);
  }

  /** Walking onto a threshold takes Joseph through. No key press and no prompt:
   *  a door you have to ASK to open is a door that feels like a chore. */
  private checkDoors(): void {
    if (this.overlay !== 'none') return;      // never fire under an open panel
    const room = this.interior();
    const rect = room ? room.mat : undefined;
    if (room) {
      if (!inRect(this.px, this.py, room.mat)) { this.doorArmed = true; return; }
      if (this.doorArmed) this.goOutside();
      return;
    }
    for (const dr of DOORS) {
      if (!inRect(this.px, this.py, dr.threshold)) continue;
      // Contact alone used to be enough, which made every doorway a trap: the
      // exit sat a few pixels from its own threshold, so stepping away pulled
      // you straight back in, and a door on a walking route swallowed anyone
      // passing. You must now be HEADING INTO the doorway.
      const heading = this.vx * dr.enter.x + this.vy * dr.enter.y;
      // NOT gated on `moving`: walking up to your own door and stopping on the
      // threshold has to let you in, or the door reads as broken. The heading
      // is cleared on every venue change, so a stale one can never fire.
      if (this.doorArmed && heading > 0.35) this.goInside(dr.to);
      return;
    }
    this.doorArmed = true;
    void rect;
  }

  private goInside(id: VenueId): void {
    const room = interiorById(id);
    if (!room) return;
    this.vx = 0; this.vy = 0;      // no stale heading may re-fire a doorway
    this.venue = id;
    this.shopLevel = this.st.workshopLevel();
    this.px = room.entry.x; this.py = room.entry.y;
    this.moveTarget = null;          // a stale tap point would walk him back out
    this.target = null;
    this.doorArmed = false;          // must step off the mat before it fires
    this.camera.bounds = { ...room.room };
    this.dipT = INTERIOR.dip; this.dipIn = true;
    sfx(this, 'door');
  }

  private goOutside(): void {
    const dr = DOORS.find((x) => x.to === this.venue);
    this.vx = 0; this.vy = 0;      // ditto on the way out
    this.venue = 'outside';
    if (dr) { this.px = dr.exit.x; this.py = dr.exit.y; }
    this.moveTarget = null;
    this.target = null;
    this.doorArmed = false;
    this.camera.bounds = { x: 0, y: 0, w: WORLD_W, h: WORLD_H };
    this.dipT = INTERIOR.dip; this.dipIn = false;
    sfx(this, 'door');
  }

  /** Hannah is at her own hearth in the evening — and her outdoor `home`
   *  anchor sits inside her house's footprint, so this also stops her standing
   *  in her own wall. */
  private hannahHome(): boolean { return this.dayT > 0.72; }

  // ── interaction resolution ───────────────────────────────────────────────────
  private resolveTarget(): Target | null {
    let best: Target | null = null; let bestD = INTERACT_RANGE * INTERACT_RANGE;
    const consider = (x: number, y: number, type: string, prompt: string, ref?: unknown) => {
      const dx = x - this.px, dy = y - this.py, dd = dx * dx + dy * dy;
      if (dd < bestD) { bestD = dd; best = { type, x, y, prompt, ref }; }
    };
    const room = this.interior();
    if (room) {
      // Indoors the outdoor list is not merely wrong, it is unreachable — the
      // interior strip sits thousands of units below the map — but keeping the
      // two lists separate is what stops "Draw water" appearing in a bedroom.
      for (const sp of room.spots) {
        if (sp.id === 'jesus' && this.jesusInShop) continue;
        // her house is never a dead room: if she is out, it says where she is
        if (sp.id === 'stock' && this.shopLevel < 6) continue;
        consider(sp.x, sp.y, `in:${sp.id}`, sp.prompt, sp);
      }
      return best;
    }
    for (const n of NODES) {
      if (this.nodeDepleted(n)) continue;
      consider(n.x, n.y, 'node', this.nodePrompt(n), n);
    }
    consider(SPOTS.workbench.x, SPOTS.workbench.y, 'bench', this.st.workbenchBuilt ? 'Open the crafting book' : 'Build your workbench');
    for (const n of NPCS) {
      const p = this.npcPos[n.id];
      consider(p.x, p.y, 'npc', this.npcPrompt(n), n);
    }
    // A STABLED animal is an ordinary target — nothing else is at the byre.
    for (const a of WORK_ANIMALS) {
      const b = this.beast[a.id];
      if (!b.out) consider(b.x, b.y, 'beast', `Bring ${a.name}`, a);
    }
    if (this.jesusInShop) consider(this.jesusPos.x, this.jesusPos.y, 'jesus', 'Teach the boy');
    consider(SPOTS.wellDraw.x, SPOTS.wellDraw.y, 'well', 'Draw water');
    if (this.st.chapter >= 5 && !this.st.communityComplete()) consider(SPOTS.community.x, SPOTS.community.y, 'community', 'Work on the well canopy');
    for (const s of PRAYER_SPOTS) if (s.id !== 'home') consider(s.x, s.y, 'prayer', s.label, s);
    for (const sc of SCROLLS) if (!this.collected.has(sc.id) && !this.st.codexFound.has(sc.entry)) consider(sc.x, sc.y, 'scroll', 'Read the scroll', sc);

    // A FOLLOWING animal walks at his heels, so it would otherwise be the
    // nearest thing to almost everything and shadow every other prompt — you
    // could never gather while it was with you. It is offered only when there
    // is nothing else in reach.
    if (best === null) {
      const out = this.beastOut();
      if (out) {
        const a = WORK_ANIMALS.find((w) => w.id === out)!;
        const b = this.beast[out];
        consider(b.x, b.y, 'beast', `Send ${a.name} home`, a);
      }
    }
    return best;
  }

  private nodeDays: Record<string, number> = {};
  private nodeDepleted(n: NodeDef): boolean { return (this.nodeDays[n.id] ?? 0) > this.st.day; }
  private depleteNode(n: NodeDef): void { this.nodeDays[n.id] = this.st.day + 1; }

  private nodePrompt(n: NodeDef): string {
    switch (n.kind) {
      case 'branch': return 'Gather a fallen branch';
      case 'tree': return 'Chop the tree';
      case 'log': return 'Split the timber log';
      case 'rock': return 'Quarry stone';
      case 'flax': return 'Cut flax for rope';
      case 'cloth': return 'Take cloth (freely given)';
      case 'logpile':
      case 'boulder': {
        const need = WORK_ANIMALS.find((w) => w.id === n.needs)!;
        // the whole mechanic is taught right here, at the moment it matters
        return this.beastOut() === n.needs
          ? (n.kind === 'logpile' ? `Load the bushel onto ${need.name}` : `Have ${need.name} take the stone`)
          : `Too heavy to carry alone — ${need.name} could bear this`;
      }
    }
  }

  private npcPrompt(n: NpcDef): string {
    const del = this.st.deliverableRequestFor(n.id);
    if (del) return `Give ${n.name} the ${recipeById(del.need)?.name}`;
    const off = this.st.offeredRequestFor(n.id);
    if (off) return `Speak with ${n.name}`;
    return `Greet ${n.name}`;
  }

  // ── perform interaction ───────────────────────────────────────────────────────
  private doInteract(tg: Target): void {
    switch (tg.type) {
      case 'node': this.gather(tg.ref as NodeDef); break;
      case 'bench': this.openBook(); break;
      case 'in:bed': this.sleep(); break;
      case 'in:table': this.meal(); break;
      case 'in:niche': this.beginPrayerHome(); break;
      case 'in:mary': this.talkMary(); break;
      case 'in:jesus': this.jesusHome(); break;
      case 'in:resident': this.visitHannah(); break;
      case 'in:stock': this.lookOverWork(); break;
      case 'npc': this.talkNpc(tg.ref as NpcDef); break;
      case 'beast': this.toggleBeast(tg.ref as WorkAnimalDef); break;
      case 'jesus': this.teachJesus(); break;
      case 'well': this.st.drinkWater(); sfx(this, 'water'); this.toast('You drink from the well. +Strength'); this.act('measure'); break;
      case 'community': this.workCommunity(); break;
      case 'prayer': this.beginPrayer(); break;
      case 'scroll': this.readScroll(tg.ref as { id: string; entry: string }); break;
    }
  }

  private act(name: string): void { this.actionName = name; this.actionT = 0.5; }

  private gather(n: NodeDef): void {
    if (n.kind === 'tree' || n.kind === 'log') {
      if (this.st.strength < COST.chop) { this.toast('Joseph is weary — rest or drink first.'); return; }
      this.chop = { node: n, pos: 0, dir: 1, done: false, resultT: 0, msg: '' };
      this.overlay = 'chop';
      return;
    }
    if (n.kind === 'logpile' || n.kind === 'boulder') {
      const need = WORK_ANIMALS.find((w) => w.id === n.needs)!;
      if (this.beastOut() !== n.needs) {
        // explain, and leave the node alone so he can come back for it
        this.toast(`Too heavy to carry alone. ${need.name[0].toUpperCase()}${need.name.slice(1)} could bear this.`);
        sfx(this, 'select');
        return;
      }
      if (this.st.strength < COST.haul) { this.toast('Joseph is weary — rest or drink first.'); return; }
      const heavy = n.kind === 'logpile' ? 'Wood' : 'Stone';
      this.st.add(n.gives, 3);
      this.st.addStrength(-COST.haul);      // lighter than by hand: the animal bears it
      this.depleteNode(n);
      this.beast[n.needs as AnimalId].loadT = 6;
      this.act('carry');
      sfx(this, 'pickup');
      this.toast(`+3 ${heavy} — ${need.name} bears the load.`);
      return;
    }
    let cost = 0; let gain: MaterialKey = n.gives; let amt = 1; let name = '';
    if (n.kind === 'branch') { cost = COST.branch; amt = 1; name = 'Wood'; this.act('measure'); }
    else if (n.kind === 'rock') { cost = COST.rock; amt = 2; name = 'Stone'; this.act('hammer'); }
    else if (n.kind === 'flax') { cost = COST.branch; amt = 1; name = 'Rope'; this.act('measure'); }
    else if (n.kind === 'cloth') { cost = 0; amt = 1; name = 'Cloth'; this.act('measure'); }
    if (this.st.strength < cost) { this.toast('Joseph is weary — rest or drink first.'); return; }
    this.st.add(gain, amt); this.st.addStrength(-cost); this.depleteNode(n);
    sfx(this, n.kind === 'rock' ? 'hammer' : 'pickup');
    this.toast(`+${amt} ${name}`);
  }

  private updateChop(dt: number): void {
    if (this.chop.done) { this.chop.resultT -= dt; if (this.chop.resultT <= 0) this.overlay = 'none'; return; }
    this.chop.pos += this.chop.dir * dt * 1.1;
    if (this.chop.pos > 1) { this.chop.pos = 1; this.chop.dir = -1; }
    if (this.chop.pos < 0) { this.chop.pos = 0; this.chop.dir = 1; }
    if (this.input.keys.tool.pressed || this.input.keys.interact.pressed) {
      const n = this.chop.node!;
      const acc = 1 - Math.min(1, Math.abs(this.chop.pos - 0.5) / 0.22);
      const clean = acc > 0.5;
      this.st.addStrength(-COST.chop);
      if (n.kind === 'tree') {
        if (clean) { this.st.add('timber', 1); this.st.add('wood', 1); this.chop.msg = 'CLEAN CUT — +1 Good Timber, +1 Wood'; }
        else { this.st.add('wood', 2); this.chop.msg = '+2 Wood'; }
      } else {
        if (clean) { this.st.add('timber', 2); this.chop.msg = 'CLEAN CUT — +2 Good Timber'; }
        else { this.st.add('timber', 1); this.chop.msg = '+1 Good Timber'; }
      }
      sfx(this, clean ? 'chopGood' : 'chop');
      this.depleteNode(n); this.act('chop');
      this.chop.done = true; this.chop.resultT = 1.4;
      this.camera.shake(clean ? 3 : 1.5, 0.2);
    }
  }

  // ── crafting book ─────────────────────────────────────────────────────────────
  private openBook(): void {
    this.overlay = 'book'; this.bookSel = 0; sfx(this, 'page');
  }
  private openInventory(): void { this.overlay = 'inventory'; sfx(this, 'page'); }

  private bookEntries(): (Recipe | 'workbench')[] {
    if (!this.st.workbenchBuilt) return ['workbench'];
    return recipesFor(this.st.workshopLevel());
  }

  private updateBook(): void {
    const k = this.input.keys;
    const entries = this.bookEntries();
    if (k.up.pressed) { this.bookSel = (this.bookSel + entries.length - 1) % entries.length; sfx(this, 'select'); }
    if (k.down.pressed) { this.bookSel = (this.bookSel + 1) % entries.length; sfx(this, 'select'); }
    if (k.book.pressed || k.back.pressed) { this.overlay = 'none'; return; }
    if (k.interact.pressed || k.tool.pressed) {
      const e = entries[this.bookSel];
      if (e === 'workbench') this.tryBuildWorkbench();
      else this.tryCraft(e);
    }
  }

  private tryBuildWorkbench(): void {
    if (!this.st.has({ ...WORKBENCH_COST })) { this.toast(`Need ${this.costText(WORKBENCH_COST)}.`); return; }
    this.st.buildWorkbench(); this.act('hammer'); sfx(this, 'craft');
    this.toast('Your workbench is built! The workshop is open.');
    this.toast(`Used ${this.costText(WORKBENCH_COST)}.`);
    this.overlay = 'none';
  }

  private tryCraft(r: Recipe): void {
    if (this.st.strength <= 0) { this.toast('Joseph is weary — rest before working.'); return; }
    if (!this.st.has(r.cost)) { this.toast('Not enough materials for that.'); return; }
    if (r.major) {
      this.mg = { recipe: r, step: 0, scores: [], pos: 0, off: Math.random() * 0.4 - 0.2, resultT: 0, quality: 0 };
      this.overlay = 'minigame';
    } else {
      const p = this.st.craft(r, 0);
      if (p) {
        sfx(this, 'craft'); this.act('saw');
        // an intermediate good reports what it MADE, not what it is
        if (r.yields) this.toast(`Made ${this.costText(r.yields)}.`);
        else this.toast(`Crafted a ${r.name}.`);
        this.toast(`Used ${this.costText(r.cost)}.`);
      }
      this.overlay = 'none';
    }
  }

  private updateMinigame(dt: number): void {
    const m = this.mg;
    if (m.resultT > 0) { m.resultT -= dt; if (m.resultT <= 0) this.overlay = 'none'; return; }
    const speed = [2.1, 2.9, 3.7][m.step];
    m.pos = (Math.sin(this.t * speed + m.off * 6) + 1) / 2;
    if (this.input.keys.tool.pressed || this.input.keys.interact.pressed) {
      const half = [0.24, 0.17, 0.12][m.step];
      const acc = 1 - Math.min(1, Math.abs(m.pos - 0.5) / half);
      m.scores.push(Math.max(0, acc));
      sfx(this, m.step === 1 ? 'saw' : m.step === 2 ? 'hammer' : 'select');
      m.step++;
      m.off = Math.random() * 0.6 - 0.3;
      if (m.step >= 3) {
        const avg = m.scores.reduce((a, b) => a + b, 0) / m.scores.length;
        const q = qualityFromScore(avg * this.st.efficiency());
        m.quality = q;
        const p = this.st.craft(m.recipe!, q);
        if (p) {
          sfx(this, q === 2 ? 'masterwork' : 'craft');
          if (q === 2) this.camera.flash('#fff3d0', 0.4);
          this.toast(`${QUALITY_NAMES[q]} ${m.recipe!.name}!`);
          this.toast(`Used ${this.costText(m.recipe!.cost)}.`);
        }
        m.resultT = 1.8;
      }
    }
  }

  // ── dialog helpers ────────────────────────────────────────────────────────────
  private openDialog(title: string, body: string[], options: DlgOption[]): void {
    this.dialog = { title, body, options, sel: 0 };
    this.overlay = 'dialog'; sfx(this, 'select');
  }
  private updateDialog(): void {
    const k = this.input.keys; const dl = this.dialog!;
    if (k.up.pressed) { dl.sel = (dl.sel + dl.options.length - 1) % dl.options.length; sfx(this, 'select'); }
    if (k.down.pressed) { dl.sel = (dl.sel + 1) % dl.options.length; sfx(this, 'select'); }
    if (k.back.pressed) { this.overlay = 'none'; return; }
    if (k.interact.pressed || k.tool.pressed) { const o = dl.options[dl.sel]; this.overlay = 'none'; o.act(); }
  }

  private talkNpc(n: NpcDef): void {
    const del = this.st.deliverableRequestFor(n.id);
    if (del) {
      const opts: DlgOption[] = [];
      opts.push({ label: `Give the ${recipeById(del.need)?.name}`, act: () => this.deliver(del.id, n, false) });
      if (del.canGiveFree) opts.push({ label: 'Give it freely, take nothing', act: () => this.deliver(del.id, n, true) });
      opts.push({ label: 'Not just yet', act: () => {} });
      this.openDialog(n.name, ['"You have finished it?"'], opts);
      return;
    }
    const off = this.st.offeredRequestFor(n.id);
    if (off) {
      this.openDialog(n.name, [`"${off.ask}"`], [
        { label: "I will do it gladly.", act: () => { this.st.acceptRequest(off.id); this.toast(`Accepted: ${off.title}`); sfx(this, 'chime'); } },
        { label: 'Perhaps another day.', act: () => {} },
      ]);
      return;
    }
    this.openDialog(n.name, [this.greeting(n)], [{ label: 'Peace be with you.', act: () => {} }]);
  }

  private greeting(n: NpcDef): string {
    const lines = [
      `"Peace to you, Joseph. Your work is honest and good."`,
      `"The whole village speaks well of your craft."`,
      `"A fine day in Nazareth. God keep you and your family."`,
      `"Blessings on your hands, carpenter."`,
    ];
    return lines[(n.name.length + this.st.day) % lines.length];
  }

  private deliver(id: string, n: NpcDef, free: boolean): void {
    const req = requestById(id)!;
    if (this.st.deliverRequest(id, free)) {
      sfx(this, free ? 'bless' : 'deliver');
      this.openDialog(n.name, [`"${req.thanks}"`, free ? '(You gave freely. +Charity)' : ''], [{ label: 'God be with you.', act: () => {} }]);
      this.toast(free ? 'Given freely. +Charity' : `Delivered: ${req.title}`);
      this.toast(`Gave ${recipeById(req.need)?.name ?? req.need}.`);
    }
  }

  /** Mary at her hearth. This carries the ONLY delivery path for the two
   *  `home: true` requests (shelf_home, chest_home — both giver 'mary', and
   *  Mary is deliberately not in NPCS). `homeProjects` gates Chapter III → IV,
   *  so if this branch is ever lost the game becomes unfinishable. */
  private talkMary(): void {
    const del = this.st.deliverableRequestFor('mary');
    const off = this.st.offeredRequestFor('mary');
    if (del) {
      this.deliver(del.id, { id: 'mary', name: 'Mary' } as NpcDef, false);
      return;
    }
    const opts: DlgOption[] = [];
    if (off) {
      opts.push({ label: 'Ask Mary what she needs', act: () => this.openDialog('Mary', [`"${off.ask}"`], [
        { label: 'I will make it.', act: () => { this.st.acceptRequest(off.id); this.toast(`Accepted: ${off.title}`); } },
        { label: 'Soon.', act: () => {} },
      ]) });
    }
    opts.push({ label: 'Sit a while with her. (+Family)', act: () => { this.st.gainFamily(1); this.st.addPeace(4); } });
    opts.push({ label: 'Leave her to her work.', act: () => {} });
    this.openDialog('Mary', ['"You have worked well today, Joseph. Come, rest a moment."'], opts);
  }

  /** Hannah's house. She is only home in the evening, so by day the room says
   *  where she is rather than offering nothing at all. */
  private visitHannah(): void {
    if (this.hannahHome()) { this.talkNpc(NPCS[0]); return; }
    this.openDialog("Hannah's House", [
      'The hearth is banked and the room is still.',
      'Hannah draws water at the well through the day; she is home by evening.',
    ], [{ label: 'Leave things as they are.', act: () => {} }]);
  }

  /** The shelf of finished pieces in the workshop store. */
  private lookOverWork(): void {
    const n = this.st.products.length;
    const body = n === 0
      ? ['The shelf is bare. Whatever you make next will stand here.']
      : [`${n} finished ${n === 1 ? 'piece waits' : 'pieces wait'} on the shelf, ready to be carried out.`];
    this.openDialog('Finished Work', body, [{ label: 'Good work is meant to be given.', act: () => {} }]);
  }

  private jesusHome(): void {
    this.st.gainFamily(1);
    const qs = [
      ['The young Jesus looks up.', '"Abba, why do you measure twice before you cut?"', 'Joseph smiles: "So that we cut only once, and cut it true."'],
      ['Jesus watches the shavings curl.', '"Will you teach me to make a yoke that is easy?"', 'Joseph: "A good yoke fits gently. I will teach you."'],
      ['The boy brings a cup of water.', '"You have worked hard today, Abba."', 'Joseph receives it with thanks.'],
    ];
    const q = qs[this.st.day % qs.length];
    this.openDialog('Jesus', q, [{ label: 'A quiet, happy moment. (+Family)', act: () => {} }]);
    sfx(this, 'chime');
  }

  private teachJesus(): void {
    this.st.gainFamily(2); this.st.addPeace(6);
    const lessons = [
      'Joseph guides the boy\'s hands along the plane, and a curl of cedar falls away. "Smooth, and patient," he says.',
      'He shows Jesus how to hold the measure true against the beam. The boy nods, careful and glad.',
      'Together they carry a finished stool to the doorway. "Good work is meant to be given," Joseph tells him.',
    ];
    this.openDialog('A Moment in the Workshop', [lessons[this.st.day % lessons.length], '(+Family)'], [{ label: 'Return to work.', act: () => {} }]);
    sfx(this, 'bless'); this.act('plane');
  }

  private meal(): void {
    this.st.eatMeal(); sfx(this, 'chime');
    this.openDialog('The Evening Meal', ['The family shares bread and olives by the lamp.', 'Joseph is restored. (+Strength, +Peace, +Family)'], [{ label: 'Give thanks and rise.', act: () => {} }]);
  }

  private beginPrayer(): void { this.prayerText = PRAYERS[(this.st.day + Math.floor(this.t)) % PRAYERS.length].replace(/\n/g, ' '); this.overlay = 'prayer'; sfx(this, 'bless'); this.act('pray'); }
  private beginPrayerHome(): void { this.beginPrayer(); }
  private updatePrayer(): void {
    if (this.confirmPressed() || this.input.keys.back.pressed) { this.st.pray(); this.toast('Peace settles over you. +Peace'); this.overlay = 'none'; }
  }

  private sleep(): void {
    this.st.sleep();
    for (const id in this.nodeDays) if (this.nodeDays[id] <= this.st.day) delete this.nodeDays[id];
    this.dayT = 0.06;
    const home = interiorById('home');
    if (this.venue === 'home' && home) {
      // You wake where you slept. Stand clear of the mat, or the first step of
      // the new day would walk you straight back out of your own front door.
      const bed = home.spots.find((sp) => sp.id === 'bed');
      this.px = bed ? bed.x : home.entry.x;
      this.py = (bed ? bed.y : home.entry.y) + 30;
      this.doorArmed = false;
    } else {
      this.px = SPOTS.workbench.x + 20; this.py = SPOTS.workbench.y + 150;
    }
    this.moveTarget = null;   // never resume walking to yesterday's tap point
    this.trail.length = 0;
    for (const a of WORK_ANIMALS) this.stableAnimal(a);   // stabled overnight
    if (this.st.freePlay) this.reofferAll();
    this.newDaySetup(false);
    sfx(this, 'chime');
  }

  private workCommunity(): void {
    if (this.st.canBuildCommunityStage()) {
      this.st.buildCommunityStage(); sfx(this, 'craft'); this.camera.shake(2, 0.2); this.act('hammer');
      this.toast(`${COMMUNITY_PROJECT.stages[this.st.communityStage - 1].name} — done!`);
      if (this.st.communityComplete()) this.toast('The well canopy is finished. Nazareth rejoices.');
    } else {
      const s = COMMUNITY_PROJECT.stages[this.st.communityStage];
      this.openDialog(COMMUNITY_PROJECT.name, [
        COMMUNITY_PROJECT.blurb,
        `Next: ${s.name}`,
        `Needs: ${this.costText(s.cost)}`,
      ], [{ label: 'I will gather what is needed.', act: () => {} }]);
    }
  }

  private revealCommunity(): void { this.toast('Nazareth asks for your help at the well.'); }

  private readScroll(sc: { id: string; entry: string }): void {
    this.collected.add(sc.id); this.st.codexFound.add(sc.entry);
    const e = CODEX.find((c) => c.id === sc.entry)!;
    sfx(this, 'scroll');
    this.openDialog("Joseph's Story", [`You found a scroll:`, `"${e.title}"`, `Read it any time with the C key.`], [{ label: 'Keep it safely.', act: () => {} }]);
  }

  // ── codex ─────────────────────────────────────────────────────────────────────
  private openCodex(): void { this.overlay = 'codex'; this.codexReading = false; sfx(this, 'page'); }
  private foundEntries(): CodexEntry[] { return CODEX.filter((c) => this.st.codexFound.has(c.id)); }
  private updateCodex(): void {
    const k = this.input.keys; const list = this.foundEntries();
    if (k.codex.pressed || k.back.pressed) { if (this.codexReading) this.codexReading = false; else this.overlay = 'none'; return; }
    if (list.length === 0) { if (this.confirmPressed()) this.overlay = 'none'; return; }
    if (!this.codexReading) {
      if (k.up.pressed) { this.codexSel = (this.codexSel + list.length - 1) % list.length; sfx(this, 'select'); }
      if (k.down.pressed) { this.codexSel = (this.codexSel + 1) % list.length; sfx(this, 'select'); }
      if (k.interact.pressed || k.tool.pressed) { this.codexReading = true; sfx(this, 'page'); }
    } else if (this.confirmPressed()) this.codexReading = false;
  }

  // ── touch ──────────────────────────────────────────────────────────────────────
  private onTap(wx: number, wy: number): void {
    // screen coords for button hit-test
    const sx = (wx - this.view.x) / this.view.w * this.hudW;
    const sy = (wy - this.view.y) / this.view.h * this.hudH;
    for (const b of this.touchBtns) {
      if (sx >= b.x && sx <= b.x + b.w && sy >= b.y && sy <= b.y + b.h) { this.pressButton(b.id); return; }
    }
    if (this.overlay === 'none') {
      // tap the world: interact if near a target, else walk there
      const dx = wx - this.px, dy = wy - this.py;
      if (this.target && Math.hypot(this.target.x - wx, this.target.y - wy) < 60 && Math.hypot(dx, dy) < INTERACT_RANGE + 40) { this.doInteract(this.target); return; }
      const room = this.interior();
      if (room) {
        // There is no path-finding: a tap outside the room would walk Joseph
        // into a wall and stick. Clamp it to the floor.
        const W = INTERIOR.wall + 10;
        this.moveTarget = {
          x: Math.max(room.room.x + W, Math.min(room.room.x + room.room.w - W, wx)),
          y: Math.max(room.room.y + W + 10, Math.min(room.room.y + room.room.h - W, wy)),
        };
      } else {
        this.moveTarget = { x: wx, y: wy };
      }
    } else {
      this.pressButton('confirm');
    }
  }
  private onSwipe(dir: string): void {
    if (this.overlay === 'book' || this.overlay === 'dialog' || this.overlay === 'codex') {
      // emulate up/down nav via synthetic key edges is hard; nudge selection directly
      const down = dir === 'down' || dir === 'right';
      if (this.overlay === 'book') { const n = this.bookEntries().length; this.bookSel = (this.bookSel + (down ? 1 : n - 1)) % n; }
      else if (this.overlay === 'dialog' && this.dialog) { const n = this.dialog.options.length; this.dialog.sel = (this.dialog.sel + (down ? 1 : n - 1)) % n; }
      else if (this.overlay === 'codex' && !this.codexReading) { const n = Math.max(1, this.foundEntries().length); this.codexSel = (this.codexSel + (down ? 1 : n - 1)) % n; }
      sfx(this, 'select');
    }
  }
  private pressButton(id: string): void {
    switch (id) {
      case 'interact': if (this.overlay === 'none' && this.target) this.doInteract(this.target); else this.confirmOverlay(); break;
      case 'tool': if (this.overlay === 'chop' || this.overlay === 'minigame') this.confirmOverlay(); else if (this.overlay === 'none' && this.target?.type === 'node') this.doInteract(this.target); break;
      case 'book': if (this.overlay === 'none') this.openBook(); else this.overlay = 'none'; break;
      case 'codex': if (this.overlay === 'none') this.openCodex(); else this.overlay = 'none'; break;
      case 'confirm': this.confirmOverlay(); break;
    }
  }
  private confirmOverlay(): void {
    // route a generic confirm through the same paths the keys use
    switch (this.overlay) {
      case 'daystart': this.overlay = 'none'; break;
      case 'dialog': if (this.dialog) { const o = this.dialog.options[this.dialog.sel]; this.overlay = 'none'; o.act(); } break;
      case 'book': { const e = this.bookEntries()[this.bookSel]; if (e === 'workbench') this.tryBuildWorkbench(); else this.tryCraft(e); break; }
      case 'chop': if (!this.chop.done) { this.simulateChopHit(); } break;
      case 'minigame': this.simulateMgHit(); break;
      case 'prayer': this.st.pray(); this.overlay = 'none'; break;
      case 'codex': if (!this.codexReading && this.foundEntries().length) this.codexReading = true; else this.overlay = 'none'; break;
      case 'inventory': this.overlay = 'none'; break;
      case 'chapter': this.overlay = 'none'; if (this.chapterShown === 5) this.revealCommunity(); break;
      default: break;
    }
  }
  private simulateChopHit(): void {
    const n = this.chop.node!; const acc = 1 - Math.min(1, Math.abs(this.chop.pos - 0.5) / 0.22); const clean = acc > 0.5;
    this.st.addStrength(-COST.chop);
    if (n.kind === 'tree') { if (clean) { this.st.add('timber', 1); this.st.add('wood', 1); this.chop.msg = 'CLEAN CUT'; } else { this.st.add('wood', 2); this.chop.msg = '+2 Wood'; } }
    else { if (clean) { this.st.add('timber', 2); this.chop.msg = 'CLEAN CUT'; } else { this.st.add('timber', 1); this.chop.msg = '+1 Timber'; } }
    sfx(this, clean ? 'chopGood' : 'chop'); this.depleteNode(n); this.chop.done = true; this.chop.resultT = 1.4;
  }
  private simulateMgHit(): void {
    const m = this.mg; if (m.resultT > 0) return;
    const half = [0.24, 0.17, 0.12][m.step]; const acc = 1 - Math.min(1, Math.abs(m.pos - 0.5) / half);
    m.scores.push(Math.max(0, acc)); m.step++;
    if (m.step >= 3) { const avg = m.scores.reduce((a, b) => a + b, 0) / m.scores.length; const q = qualityFromScore(avg); m.quality = q; this.st.craft(m.recipe!, q); this.toast(`${QUALITY_NAMES[q]} ${m.recipe!.name}!`); m.resultT = 1.6; }
  }

  private startEnding(): void { this.endT = 0.001; this.overlay = 'ending'; }

  private toast(text: string): void { this.toasts.push({ text, ttl: 2.6 }); }
  private costText(cost: Partial<Record<MaterialKey, number>>): string {
    return Object.entries(cost).map(([k, v]) => `${v} ${MATERIALS[k as MaterialKey].name}`).join(', ');
  }

  // ══ DRAW WORLD ═════════════════════════════════════════════════════════════════
  override draw(d: Draw): void {
    this.view = { x: this.camera.x, y: this.camera.y, w: this.width, h: this.height };
    const room = this.interior();
    if (room) { this.drawInterior(d, room); return; }
    this.drawGround(d);
    this.drawDepthSorted(d);
    if (this.st.chapter >= 5) this.drawCommunity(d);
    this.drawScrolls(d);
    this.drawBirds(d);
    this.drawTargetHint(d);
    this.drawDayTint(d);
    if (this.overlay === 'ending') { /* dim handled in hud */ }
  }

  private drawInterior(d: Draw, it: Interior): void {
    const v = this.view;
    // The room is smaller than the view, so fill the frame first — otherwise
    // the gap around it is the raw clear colour.
    // Anything the room doesn't cover reads as the rest of the house in shadow,
    // in the timber palette rather than a flat void.
    d.rect(v.x, v.y, v.w, v.h, R.shade(C.timberDark, -34));
    R.mottle(d, v.x, v.y, v.w, v.h, '#000000', 0.25, 200);
    INT.room(d, it, this.dayT, this.t);
    for (const f of it.furniture) {
      if (f.kind === 'window') INT.windowLight(d, it, f, this.dayT);
    }

    // Furniture and people share one depth sort, as outside.
    const items: { y: number; f: () => void }[] = [];
    const overhead: Furniture[] = [];
    for (const f of it.furniture) {
      if (f.minLevel && this.shopLevel < f.minLevel) continue;
      // Joseph's own work stands here only once he has actually built it and
      // given it to Mary — the room on day thirty is not the room on day one.
      if (f.requires && !this.st.requestDone(f.requires)) continue;
      if (f.overhead) { overhead.push(f); continue; }
      items.push({ y: f.y + f.h, f: () => INT.piece(d, f, this.t, this.st.products.length) });
    }
    if (it.id === 'home') {
      const mary = it.spots.find((sp) => sp.id === 'mary');
      if (mary) items.push({ y: mary.y, f: () => R.person(d, mary.x, mary.y, { robe: C.robeMary, tunic: C.veilMary, veil: C.veilMary, facing: 'down', scale: 0.98, smile: true }) });
      const jes = it.spots.find((sp) => sp.id === 'jesus');
      if (jes && !this.jesusInShop) items.push({ y: jes.y, f: () => R.person(d, jes.x, jes.y, { robe: C.tunicJesus, tunic: C.white, facing: 'down', scale: 0.6, hair: C.hair, smile: true }) });
    }
    if (it.id === 'hannah' && this.hannahHome()) {
      const sp = it.spots.find((q) => q.id === 'resident');
      const n = NPCS[0];
      if (sp) items.push({ y: sp.y, f: () => {
        R.person(d, sp.x, sp.y, { robe: n.color, tunic: R.shade(n.color, 26), facing: 'down', scale: 0.96 });
        if (this.st.deliverableRequestFor(n.id)) R.marker(d, sp.x, sp.y - 82, this.t, 'deliver');
        else if (this.st.offeredRequestFor(n.id)) R.marker(d, sp.x, sp.y - 82, this.t, 'ask');
      } });
    }
    items.push({ y: this.py, f: () => this.drawJoseph(d) });
    items.sort((a, b) => a.y - b.y);
    for (const q of items) q.f();

    INT.interiorLight(d, it, this.dayT, this.t);
    for (const f of overhead) INT.beams(d, f);   // the roof, over everything
    this.drawTargetHint(d);
    this.drawDayTint(d);
  }

  private night(): boolean { return this.dayT >= 0.86; }

  private drawGround(d: Draw): void {
    const v = this.view;
    d.rect(v.x, v.y, v.w, v.h, C.grass);
    // Regions, in world space. Their edges are feathered: drawn as plain rects
    // they ended on straight lines and right-angled corners in open grass,
    // which read as rectangles laid over the map rather than as terrain.
    // The hills and fields wash past their shared line in both directions so
    // they cross-fade into each other instead of leaving a pale gap.
    const ov = REGION.overlap;
    R.featherRect(d, 0, 0, WORLD_W, 640, C.leaf, 0.35, REGION.grove,
      { bottom: true });                                     // grove (soft green)
    R.featherRect(d, 0, 606, WORLD_W, 88, C.grassLight, 0.3, REGION.groveEdge,
      { top: true, bottom: true });                          // grove edge
    R.featherRect(d, 0, 1400, 1050 + ov, WORLD_H - 1400, C.sand, 0.5, REGION.hills,
      { top: true, right: true });                           // hills (dusty)
    R.featherRect(d, 1050 - ov, 1400, WORLD_W - 1050 + ov, WORLD_H - 1400, C.olive, 0.3,
      REGION.fields, { top: true, left: true });             // fields
    R.featherRect(d, 1120, 700, 1120, 700, C.sand, 0.5, REGION.plaza,
      { top: true, right: true, bottom: true, left: true }); // village plaza
    // Two world-locked noise passes over the ground: broad sun-warmed patches,
    // then a fine tooth. Both stay low — this is meant to read as a painted
    // surface at a glance, not as haze sitting on top of the world.
    R.groundTexture(d, v.x, v.y, v.w, v.h, R.shade(C.grassLight, 14), 0.11, 230, 'mottle');
    R.groundTexture(d, v.x, v.y, v.w, v.h, R.shade(C.grassDark, -20), 0.09, 58, 'grain');
    // paths
    this.path(d, SPOTS.workbench.x, SPOTS.workbench.y, 1500, 1050, 34);   // home→village
    this.path(d, 1500, 1050, 900, 360, 30);                                // village→grove
    this.path(d, 700, 1100, 600, 1620, 30);                                // →hills
    this.path(d, 1560, 1200, 1900, 1560, 30);                              // →fields
    this.path(d, 1500, 1050, 1990, 860, 26);                               // →synagogue
    // tufts / flowers
    for (const tf of this.tufts) {
      if (!d.visible(tf.x - 6, tf.y - 6, 12, 12)) continue;
      if (tf.k < 0.55) { d.line(tf.x, tf.y, tf.x - 2, tf.y - 6 - tf.k * 6, 1.4, C.grassDark, 0.8); d.line(tf.x + 2, tf.y, tf.x + 3, tf.y - 5 - tf.k * 5, 1.4, C.grassDark, 0.8); }
      else if (tf.k < 0.8) { d.circle(tf.x, tf.y - 4, 2.4, tf.k < 0.68 ? '#e8b6c0' : C.goldLight, 0.9); d.circle(tf.x, tf.y, 1.4, C.grassDark, 0.7); }
      else { d.circle(tf.x, tf.y, 3, C.sandDark, 0.4); }
    }
  }

  private path(d: Draw, x0: number, y0: number, x1: number, y1: number, w: number): void {
    d.line(x0, y0, x1, y1, w * 1.18, R.shade(C.path, -14), 0.35);   // scuffed verge
    d.line(x0, y0, x1, y1, w, C.path, 0.9);
    d.line(x0, y0, x1, y1, w * 0.6, C.sand, 0.7);
    // trodden stones, spaced along the path and stable from day to day
    const n = Math.floor(Math.hypot(x1 - x0, y1 - y0) / 46);
    for (let i = 1; i < n; i++) {
      const f = i / n;
      const px = x0 + (x1 - x0) * f, py = y0 + (y1 - y0) * f;
      const j = R.hash01(px, py);
      d.circle(px + (j - 0.5) * w * 0.7, py + (R.hash01(py, px) - 0.5) * w * 0.7, 1.4 + j * 2.2, R.shade(C.sand, -22), 0.45);
    }
  }

  private drawDepthSorted(d: Draw): void {
    const items: { y: number; f: () => void }[] = [];
    const night = this.night();
    for (const s of STRUCTS) {
      if (!d.visible(s.x - 40, s.y - 40, s.w + 80, s.h + 120)) continue;
      // A building sorts on its base. A flat plot has no height to stand behind,
      // so it sorts on its TOP edge — otherwise the soil paints over the very
      // person who is meant to be kneeling in it.
      // A building sorts on its base. A flat plot sorts on its top edge, and so
      // does an OPEN SHELTER — the byre is a roof you stand under, so anything
      // beneath it must draw in front of it, not behind.
      const key = s.kind === 'garden' ? s.y + 10 : s.kind === 'byre' ? s.y + 44 : s.y + s.h;
      items.push({ y: key, f: () => R.drawStruct(d, s, this.st.workshopLevel(), this.t, night) });
    }
    for (const n of NODES) {
      if (!d.visible(n.x - 60, n.y - 120, 120, 160)) continue;
      const dep = this.nodeDepleted(n);
      items.push({ y: n.y, f: () => this.drawNode(d, n, dep) });
    }
    // Mary is indoors at her hearth now. Jesus is only out here on the days he
    // comes to the shop; otherwise he is at home with her.
    if (this.jesusInShop) {
      items.push({ y: this.jesusPos.y, f: () => R.person(d, this.jesusPos.x, this.jesusPos.y, { robe: C.tunicJesus, tunic: C.white, facing: 'down', scale: 0.6, hair: C.hair, smile: true }) });
    }
    for (const n of NPCS) {
      if (n.id === 'hannah' && this.hannahHome()) continue;   // she is indoors
      const p = this.npcPos[n.id];
      if (d.visible(n.work.x - 60, n.work.y - 70, 120, 100)) {
        items.push({ y: n.work.y, f: () => this.drawNpcStation(d, n) });
      }
      if (!d.visible(p.x - 40, p.y - 90, 80, 120)) continue;
      items.push({ y: p.y, f: () => this.drawNpc(d, n, p) });
    }
    // The working animals — panniers on, so they read as working stock rather
    // than the village's ambient donkeys.
    for (const a of WORK_ANIMALS) {
      const b = this.beast[a.id];
      if (!d.visible(b.x - 46, b.y - 70, 92, 92)) continue;
      items.push({ y: b.y, f: () => {
        const seed = a.id === 'ox' ? 71 : 23;
        if (a.id === 'ox') R.drawOx(d, b.x, b.y, this.t + b.walk, seed, b.loadT > 0);
        else R.drawDonkey(d, b.x, b.y, this.t + b.walk, seed);
        R.panniers(d, b.x, b.y, seed, b.loadT > 0);
      } });
    }
    // Ambient animals. Ground-dwellers depth-sort with everything else; birds
    // are in the air, so they are drawn last, over the top.
    for (let i = 0; i < ANIMALS.length; i++) {
      const a = ANIMALS[i];
      if (a.kind === 'bird') continue;
      const q = this.animalAt(a, i);
      if (!d.visible(q.x - 40, q.y - 60, 80, 80)) continue;
      items.push({ y: q.y, f: () => this.drawAnimal(d, a, q, i) });
    }
    // Joseph
    items.push({ y: this.py, f: () => this.drawJoseph(d) });

    items.sort((a, b) => a.y - b.y);
    for (const it of items) it.f();
  }

  /** Where an animal is right now: its anchor plus a slow two-rate drift. No
   *  stored state, so it can never desync — the clock is the whole simulation. */
  private animalAt(a: AnimalDef, i: number): { x: number; y: number } {
    const ph = i * 1.9;
    const slow = a.kind === 'donkey' ? 0.05 : a.kind === 'chicken' ? 0.5 : 0.17;
    return {
      x: a.x + Math.cos(this.t * slow + ph) * a.r,
      y: a.y + Math.sin(this.t * slow * 0.72 + ph * 1.4) * a.r * 0.55,
    };
  }

  private drawAnimal(d: Draw, a: AnimalDef, q: { x: number; y: number }, i: number): void {
    const seed = a.x * 0.31 + a.y * 0.17 + i;
    switch (a.kind) {
      case 'goat': R.drawGoat(d, q.x, q.y, this.t, seed); break;
      case 'sheep': R.drawSheep(d, q.x, q.y, this.t, seed); break;
      case 'chicken': R.drawChicken(d, q.x, q.y, this.t, seed); break;
      case 'donkey': R.drawDonkey(d, q.x, q.y, this.t, seed); break;
      default: break;
    }
  }

  /** Birds over the grove — drawn above the world, never depth-sorted, because
   *  they are in the air. They rise and settle on a long, slow cycle. */
  private drawBirds(d: Draw): void {
    for (let i = 0; i < ANIMALS.length; i++) {
      const a = ANIMALS[i];
      if (a.kind !== 'bird') continue;
      const q = this.animalAt(a, i);
      if (!d.visible(q.x - 30, q.y - 90, 60, 110)) continue;
      const seed = a.x * 0.31 + a.y * 0.17 + i;
      // altitude eases to nearly nothing now and then, as if landing
      const alt = 20 + 44 * (0.5 + 0.5 * Math.sin(this.t * 0.23 + i * 2.1));
      R.drawBird(d, q.x, q.y, alt, this.t, seed);
    }
  }

  private drawNode(d: Draw, n: NodeDef, dep: boolean): void {
    switch (n.kind) {
      case 'branch': R.drawTree(d, n.x, n.y, 'branch', dep); break;
      case 'tree': R.drawTree(d, n.x, n.y, 'tree', dep); break;
      case 'log': R.drawTree(d, n.x, n.y, 'log', dep); break;
      case 'rock': R.drawRock(d, n.x, n.y, dep); break;
      case 'flax': R.drawFlax(d, n.x, n.y, dep); break;
      case 'cloth': R.drawClothStand(d, n.x, n.y, dep); break;
      case 'logpile': R.drawLogpile(d, n.x, n.y, dep); break;
      case 'boulder': R.drawBoulder(d, n.x, n.y, dep); break;
    }
    if (!dep && this.st.chapter >= 1) {
      // subtle gather hint dot for wood in chapter 1
    }
  }

  private drawNpc(d: Draw, n: NpcDef, p: { x: number; y: number; facing: R.Facing; walk: number; moving: boolean }): void {
    const child = n.id === 'child_ruth';
    // Ruth skips rather than walks — a child reads as a child from her gait.
    const bob = p.moving ? Math.abs(Math.sin(p.walk)) * (child ? -4 : -2.4) : 0;
    R.person(d, p.x, p.y, {
      robe: n.color, tunic: R.shade(n.color, 26),
      facing: p.facing, bob, armSwing: p.moving ? p.walk : 0,
      action: p.moving ? 'walk' : 'idle',
      beard: !child && n.id !== 'hannah' && n.id !== 'susanna' && n.id !== 'naomi',
      gray: n.id === 'rabbi',
      scale: child ? 0.62 : 0.96,
    });
    if (this.st.deliverableRequestFor(n.id)) R.marker(d, p.x, p.y - (child ? 52 : 82), this.t, 'deliver');
    else if (this.st.offeredRequestFor(n.id)) R.marker(d, p.x, p.y - (child ? 52 : 82), this.t, 'ask');
  }

  /** The thing each villager is working at, drawn on their station rather than
   *  carried — so it stays put when they wander off and reads as their place. */
  private drawNpcStation(d: Draw, n: NpcDef): void {
    const w = n.work;
    switch (n.role) {
      case 'water':  R.clayJar(d, w.x + 26, w.y + 10, 1.05, C.roofDark); break;
      case 'market': R.basketProp(d, w.x + 28, w.y + 8, 1.1); R.potStack(d, w.x - 30, w.y + 6, 0.9, w.x); break;
      case 'weave':  R.loomProp(d, w.x + 30, w.y + 6, 1, w.x); break;
      case 'field':  R.hoeProp(d, w.x + 26, w.y + 6, 1); R.sackProp(d, w.x - 28, w.y + 8, 0.95); break;
      case 'teach':  R.lecternProp(d, w.x + 26, w.y + 8, 1); break;
      case 'garden': R.basketProp(d, w.x + 30, w.y + 10, 0.95); break;
      default: break;   // Ruth is playing; she needs no equipment
    }
  }

  private drawJoseph(d: Draw): void {
    let action = 'idle'; let carry: string | undefined;
    if (this.actionT > 0) action = this.actionName;
    else if (this.moving) action = 'walk';
    const bob = this.moving ? Math.abs(Math.sin(this.walkPhase)) * -3 : 0;
    R.person(d, this.px, this.py, {
      robe: C.robeJoseph, tunic: C.tunicJoseph, skin: C.skin, hair: C.hair, gray: true, beard: true,
      smile: true, sash: true,
      facing: this.facing, bob, armSwing: this.moving ? this.walkPhase : 0, action, carry,
    });
  }

  private drawCommunity(d: Draw): void {
    const cx = SPOTS.community.x, cy = SPOTS.community.y;
    const s = this.st.communityStage;
    if (s <= 0 && !this.st.communityComplete()) {
      // marked build site
      d.ring(cx, cy, 40, 3, C.gold, 0.4);
    }
    if (s >= 1) { d.rect(cx - 60, cy + 30, 120, 10, C.stoneDark); d.rect(cx - 56, cy + 28, 112, 4, C.stoneWall); } // footings
    if (s >= 2) { for (const dx of [-52, 52]) { d.rect(cx + dx - 5, cy - 30, 10, 62, C.timberDark); } } // posts
    if (s >= 3) { d.rect(cx - 60, cy - 34, 120, 8, C.timber); } // beams
    if (s >= 4) { d.fill([{ x: cx - 72, y: cy - 30 }, { x: cx, y: cy - 58 }, { x: cx + 72, y: cy - 30 }], C.roof); d.circle(cx, cy - 58, 6, C.gold); }
    if (this.st.chapter >= 5 && !this.st.communityComplete()) R.marker(d, cx, cy - 70, this.t, 'gather');
  }

  private drawScrolls(d: Draw): void {
    for (const sc of SCROLLS) {
      if (this.collected.has(sc.id) || this.st.codexFound.has(sc.entry)) continue;
      if (!d.visible(sc.x - 20, sc.y - 20, 40, 40)) continue;
      R.drawScroll(d, sc.x, sc.y, this.t);
    }
  }

  private drawTargetHint(d: Draw): void {
    if (this.overlay !== 'none' || !this.target) return;
    const tg = this.target;
    d.ring(tg.x, tg.y - 6, 20 + Math.sin(this.t * 4) * 2, 2.5, C.gold, 0.6);
  }

  private drawDayTint(d: Draw): void {
    const v = this.view; const dt = this.dayT;
    let col = '#000000'; let a = 0;
    if (dt < 0.28) { col = '#ffd9a0'; a = 0.10 * (1 - dt / 0.28); }
    else if (dt < 0.62) { a = 0; }
    else if (dt < 0.86) { col = '#ff9a52'; a = 0.20 * ((dt - 0.62) / 0.24); }
    else { col = '#1a1c3a'; a = 0.42; }
    // Indoors the sky's tint only half applies — lamps and a hearth are lit,
    // and a room you have just walked into should feel like a reward.
    if (this.venue !== 'outside') a *= INTERIOR.lightFloor;
    // A standing warm wash under the day cycle — Nazareth is never cold-lit.
    d.rect(v.x, v.y, v.w, v.h, '#ffcf8f', this.venue === 'outside' ? 0.03 : 0.06);
    if (a > 0) d.rect(v.x, v.y, v.w, v.h, col, a);
    // a few path lamps at night — outdoor coordinates, outdoors only
    if (this.venue === 'outside' && this.night()) {
      for (const [lx, ly] of [[1500, 1050], [700, 1100], [1560, 1210], [430, 1130]] as [number, number][]) {
        if (d.visible(lx - 40, ly - 40, 80, 80)) R.lampGlow(d, lx, ly - 10, 44, this.t);
      }
    }
  }

  // ══ DRAW HUD ═════════════════════════════════════════════════════════════════
  override drawHud(d: Draw): void {
    this.hudW = d.w; this.hudH = d.h;
    this.drawStatus(d);
    if (this.overlay === 'none') { this.drawPrompt(d); this.drawTouch(d); }
    this.drawToasts(d);
    if (this.dipT > 0) {
      // Eyes adjusting, not a fade to black: gold going in, daylight coming out.
      const f = this.dipT / INTERIOR.dip;
      const a = Math.sin(Math.min(1, f) * Math.PI) * 0.5;
      d.rect(0, 0, d.w, d.h, this.dipIn ? C.lamp : C.white, a);
    }
    switch (this.overlay) {
      case 'daystart': this.drawDayStart(d); break;
      case 'book': this.drawBook(d); break;
      case 'inventory': this.drawInventory(d); break;
      case 'dialog': this.drawDialog(d); break;
      case 'codex': this.drawCodex(d); break;
      case 'chop': this.drawChop(d); break;
      case 'minigame': this.drawMinigame(d); break;
      case 'prayer': this.drawPrayer(d); break;
      case 'chapter': this.drawChapter(d); break;
      case 'ending': this.drawEnding(d); break;
    }
  }

  private phaseName(): string {
    const t = this.dayT;
    return t < 0.28 ? 'Morning' : t < 0.62 ? 'Afternoon' : t < 0.86 ? 'Sunset' : 'Evening';
  }

  private drawStatus(d: Draw): void {
    const { margin: M, pad: PAD } = HUD;
    const F = this.hudRect(d);          // the frame the player can actually see

    // top-left panel: meters. The three tallies and the tool name all grow with
    // play (`Family 8` becomes `Family 128`), so they are flowed from measured
    // widths rather than parked at fixed x's, and the panel grows if they ever
    // outrun it. At ordinary values this lands exactly where it always did.
    const cx0 = F.x + M + PAD;                 // content left edge
    const tallies: Array<[string, string]> = [
      [`Craft ${this.st.craftsmanship}`, C.gold],
      [`Charity ${this.st.charity}`, '#d98a6a'],
      [`Family ${this.st.family}`, '#a6c07a'],
    ];
    const tallyW = tallies.map(([t]) => this.fSmall.measure(t));
    const tallySum = tallyW.reduce((a, b) => a + b, 0);
    const toolText = `Tool: ${this.tools[this.tool]}  (Q/E)`;
    const statusContentW = Math.max(
      246,                                     // the meter label + bar row
      tallySum + HUD.statMinGap * (tallies.length - 1),
      this.fSmall.measure(toolText),
    );
    const statusW = Math.max(HUD.statusW, Math.ceil(statusContentW) + PAD * 2);

    d.panel(F.x + M, F.y + M, statusW, HUD.statusH, 'glossy');
    d.rect(F.x + M, F.y + M, statusW, HUD.statusH, C.parchment, 0.12);
    const barW = statusW - 100;                // keeps the original 8px right margin
    const cy0 = F.y + M;                       // the panel's own top edge
    this.meter(d, cx0, cy0 + 16, 'Strength', this.st.strength / STRENGTH_MAX, '#c86a4a', barW);
    this.meter(d, cx0, cy0 + 44, 'Peace', this.st.peace / PEACE_MAX, '#6f9ac0', barW);

    // Spread the tallies across the content box; the gap absorbs longer numbers.
    const tallyGap = Math.max(HUD.statMinGap, (statusW - PAD * 2 - tallySum) / (tallies.length - 1));
    let tx = cx0;
    for (let i = 0; i < tallies.length; i++) {
      d.text(this.fSmall, tallies[i][0], tx, cy0 + 76, { color: tallies[i][1] });
      tx += tallyW[i] + tallyGap;
    }
    d.text(this.fSmall, toolText, cx0, cy0 + 94, { color: C.parchment });

    // top-right: day + chapter goal. The objective line is written copy of no
    // fixed length ("Gather 6 Wood + 2 Stone, then build at the workshop"), so
    // the panel is laid out from MEASURED text: wrap to the widest inner width
    // the frame allows, then shrink the panel to the longest line that came
    // out. Nothing here is a fixed width, so nothing can spill past the edge.
    const ch = CHAPTERS[this.st.chapter - 1];
    const dayLine = `Day ${this.st.day} · ${this.phaseName()}`;

    // Widest the panel may be: never past the left meters panel, never past the
    // frame, and never wider than looks right on a big monitor.
    const roomToLeft = F.w - M - (M + statusW + HUD.gap);
    const panelMaxW = Math.max(HUD.goalMinW, Math.min(HUD.goalMaxW, roomToLeft));
    const innerMax = panelMaxW - PAD * 2;

    const chapterLines = R.wrapText(this.fSmall, `Chapter ${ch.roman}: ${ch.title}`, innerMax);
    const goalLines = R.wrapText(this.fSmall, this.st.goalProgress(), innerMax);

    // Shrink to fit: the panel is only as wide as its longest rendered line.
    let inner = this.fH2.measure(dayLine);
    for (const line of [...chapterLines, ...goalLines]) inner = Math.max(inner, this.fSmall.measure(line));
    const panelW = Math.max(HUD.goalMinW, Math.min(panelMaxW, Math.ceil(inner) + PAD * 2));

    const lhH2 = this.fH2.lineHeight, lhSm = this.fSmall.lineHeight;
    const panelH = Math.ceil(
      PAD - 4 + lhH2 + HUD.lineGap
      + chapterLines.length * (lhSm + HUD.lineGap)
      + HUD.blockGap
      + goalLines.length * (lhSm + HUD.lineGap)
      + PAD - 6,
    );

    const px = F.x + F.w - M - panelW;
    const rx = px + panelW - PAD;   // right edge the lines align to
    d.panel(px, F.y + M, panelW, panelH, 'glossy');
    d.rect(px, F.y + M, panelW, panelH, C.parchment, 0.12);

    let ty = F.y + M + PAD - 4;
    d.text(this.fH2, dayLine, rx, ty, { align: 'right', color: C.white });
    ty += lhH2 + HUD.lineGap;
    for (const line of chapterLines) {
      d.text(this.fSmall, line, rx, ty, { align: 'right', color: C.goldLight });
      ty += lhSm + HUD.lineGap;
    }
    ty += HUD.blockGap;
    for (const line of goalLines) {
      d.text(this.fSmall, line, rx, ty, { align: 'right', color: C.parchment });
      ty += lhSm + HUD.lineGap;
    }

    // bottom controls hint
    if (this.overlay === 'none') d.text(this.fSmall, 'WASD move · X interact · Space tool · Tab book · C codex', F.x + F.w / 2, F.y + F.h - 24, { align: 'center', color: C.parchment, alpha: 0.7 });
  }

  private meter(d: Draw, x: number, y: number, label: string, f: number, col: string, bw = 168): void {
    d.text(this.fSmall, label, x, y, { color: C.ink });
    const bx = x + 78;
    d.rect(bx, y + 2, bw, 12, '#00000030');
    d.rect(bx, y + 2, bw * Math.max(0, Math.min(1, f)), 12, col);
    d.rect(bx, y + 2, bw, 3, '#ffffff30');
  }

  private drawPrompt(d: Draw): void {
    if (!this.target) return;
    const label = `X  ${this.target.prompt}`;
    const F = this.hudRect(d);
    const w = Math.min(Math.ceil(this.fBody.measure(label)) + 30, F.w - HUD.margin * 2);
    const x = F.x + F.w / 2 - w / 2, y = F.y + F.h - 80;
    d.panel(x, y, w, 34, 'glossy');
    d.rect(x, y, w, 34, C.gold, 0.14);
    d.text(this.fBody, label, d.w / 2, y + 7, { align: 'center', color: C.white });
  }

  private drawToasts(d: Draw): void {
    for (let i = 0; i < this.toasts.length; i++) {
      const ts = this.toasts[i];
      const a = Math.min(1, ts.ttl);
      const F = this.hudRect(d);
      const y = F.y + F.h - 122 - i * 30;
      const w = Math.min(F.w - HUD.margin * 2, Math.ceil(this.fBody.measure(ts.text)) + PANEL.pad * 2);
      d.rect(F.x + F.w / 2 - w / 2, y, w, 26, C.dusk, 0.7 * a);
      d.text(this.fBody, ts.text, F.x + F.w / 2, y + 4, { align: 'center', color: C.goldLight, alpha: a });
    }
  }

  private drawTouch(d: Draw): void {
    // only show if a touch pointer is (or was) active
    const p = this.input.pointer;
    if (!p || !p.isTouch) { this.touchBtns = []; return; }
    this.touchBtns = [];
    const btn = (id: string, x: number, y: number, label: string) => {
      const w = 84, h = 60;
      this.touchBtns.push({ id, x, y, w, h, label });
      d.panel(x, y, w, h, 'glossy'); d.rect(x, y, w, h, C.gold, 0.14);
      d.text(this.fSmall, label, x + w / 2, y + h / 2 - 8, { align: 'center', color: C.white });
    };
    btn('interact', d.w - 100, d.h - 150, 'X');
    btn('tool', d.w - 194, d.h - 150, 'Tool');
    btn('book', d.w - 100, d.h - 220, 'Book');
    btn('codex', d.w - 194, d.h - 220, 'Story');
  }

  // ── overlay panels ──────────────────────────────────────────────────────────
  private scrim(d: Draw, a = 0.55): void { d.rect(0, 0, d.w, d.h, C.dusk, a); }
  private parchment(d: Draw, x: number, y: number, w: number, h: number): void {
    d.rect(x - 6, y - 6, w + 12, h + 12, C.parchmentEdge);
    d.rect(x, y, w, h, C.parchment);
    d.rect(x, y, w, 5, C.gold, 0.5);
    d.rect(x, y + h - 5, w, 5, C.gold, 0.5);
  }

  /** Wrap to a PIXEL width using the font's own metrics. The old wrapper
   *  counted characters, which is why copy ran over the frame — 56 characters
   *  of 'W' is not 56 characters of 'i'. */
  private wrapPx(font: BitmapFont, text: string, maxW: number): string[] {
    return R.wrapText(font, text, maxW);
  }

  /** Draw a block of copy inside a panel, wrapped to its inner width, and
   *  return the y just past it. The one place panel text is laid out. */
  private panelText(d: Draw, font: BitmapFont, text: string, x: number, y: number,
                    innerW: number, lh: number, color: string): number {
    let ly = y;
    for (const ln of this.wrapPx(font, text, innerW)) {
      d.text(font, ln, x, ly, { color });
      ly += lh;
    }
    return ly;
  }

  private drawDayStart(d: Draw): void {
    this.scrim(d, 0.5);
    const w = 520, h = 360, x = d.w / 2 - w / 2, y = d.h / 2 - h / 2;
    this.parchment(d, x, y, w, h);
    d.text(this.fH1, 'A New Day in Nazareth', d.w / 2, y + 26, { align: 'center', color: C.ink });
    d.text(this.fBody, `Day ${this.st.day} · Morning light fills the home.`, d.w / 2, y + 70, { align: 'center', color: C.timberDark });
    d.text(this.fH2, 'Today you might:', d.w / 2, y + 116, { align: 'center', color: C.ink });
    const tasks = this.st.dailyTasks();
    for (let i = 0; i < tasks.length; i++) d.text(this.fBody, `•  ${tasks[i]}`, x + 60, y + 156 + i * 32, { color: C.timberDark });
    d.text(this.fSmall, 'There is no hurry. Work, pray, provide — in your own good time.', d.w / 2, y + h - 56, { align: 'center', color: C.timber });
    d.text(this.fBody, 'Press X to begin', d.w / 2, y + h - 30, { align: 'center', color: C.gold, alpha: 0.7 + Math.sin(this.t * 3) * 0.3 });
  }

  private drawBook(d: Draw): void {
    this.scrim(d);
    const w = 560, h = 470, x = d.w / 2 - w / 2, y = d.h / 2 - h / 2;
    this.parchment(d, x, y, w, h);
    d.text(this.fH1, "The Crafting Book", d.w / 2, y + 20, { align: 'center', color: C.ink });
    const lvl = this.st.workshopLevel();
    d.text(this.fSmall, this.st.workbenchBuilt ? `Workshop level ${lvl} · Craftsmanship ${this.st.craftsmanship}` : 'Your workshop is not yet built.', d.w / 2, y + 56, { align: 'center', color: C.timber });
    const entries = this.bookEntries();
    const listY = y + 86;
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i]; const on = i === this.bookSel; const ry = listY + i * 30;
      if (on) { d.rect(x + 16, ry - 3, w - 32, 28, C.gold, 0.2); }
      if (e === 'workbench') {
        d.text(this.fBody, 'Build Workbench', x + 30, ry, { color: on ? C.ink : C.timberDark });
        const ok = this.st.has({ wood: 6, stone: 2 });
        d.text(this.fSmall, '6 Wood, 2 Stone', x + w - 30, ry + 2, { align: 'right', color: ok ? '#3c7a3c' : '#a05a45' });
      } else {
        const ok = this.st.canCraft(e);
        d.text(this.fBody, e.name + (e.major ? '  *' : ''), x + 30, ry, { color: on ? C.ink : ok ? C.timberDark : '#9a8a72' });
        d.text(this.fSmall, this.costText(e.cost), x + w - 30, ry + 2, { align: 'right', color: ok ? '#3c7a3c' : '#a05a45' });
      }
    }
    // detail of selected
    const sel = entries[this.bookSel];
    const dy = y + h - 92;
    d.line(x + 20, dy - 8, x + w - 20, dy - 8, 1, C.parchmentEdge);
    if (sel === 'workbench') d.text(this.fSmall, 'Every good workshop begins with a sturdy bench.', x + 30, dy, { color: C.timber });
    else { for (const [i, ln] of this.wrapPx(this.fSmall, sel.desc, 560 - PANEL.pad * 2).entries()) d.text(this.fSmall, ln, x + PANEL.pad, dy + i * 20, { color: C.timber }); if (sel.major) d.text(this.fSmall, '* A major piece — you will measure, cut and join it.', x + 30, dy + 22, { color: C.gold }); }
    d.text(this.fSmall, 'Up/Down choose · X craft · Tab close', d.w / 2, y + h - 22, { align: 'center', color: C.timber });
  }

  private drawInventory(d: Draw): void {
    this.scrim(d);
    const w = 560, h = 420, x = d.w / 2 - w / 2, y = d.h / 2 - h / 2;
    this.parchment(d, x, y, w, h);
    d.text(this.fH1, "Joseph's Stores", d.w / 2, y + 20, { align: 'center', color: C.ink });
    const mats = Object.keys(MATERIALS) as MaterialKey[];
    d.text(this.fH2, 'Materials', x + PANEL.pad, y + 62, { color: C.ink });
    // Fixed-pitch columns collided as soon as one label was long ("Good Timber"
    // ran into "Stone"). Pitch comes from the widest label actually present.
    const gridW = w - PANEL.pad * 2;
    const labelW = Math.max(...mats.map((k) => this.fBody.measure(`${MATERIALS[k].name}: ${this.st.materials[k]}`)));
    const pitch = Math.ceil(labelW) + 40;
    const cols = Math.max(1, Math.min(3, Math.floor(gridW / pitch)));
    for (let i = 0; i < mats.length; i++) {
      const mx = x + PANEL.pad + (i % cols) * Math.floor(gridW / cols);
      const my = y + 96 + Math.floor(i / cols) * 30;
      const m = MATERIALS[mats[i]];
      d.circle(mx + 7, my + 8, 7, m.color);
      d.text(this.fBody, `${m.name}: ${this.st.materials[mats[i]]}`, mx + 22, my, { color: C.timberDark });
    }
    d.text(this.fH2, 'Finished work', x + PANEL.pad, y + 190, { color: C.ink });
    if (this.st.products.length === 0) d.text(this.fSmall, 'None just now — the bench is quiet.', x + PANEL.pad, y + 220, { color: C.timber });
    const counts: Record<string, number> = {};
    for (const p of this.st.products) counts[p.recipe] = (counts[p.recipe] ?? 0) + 1;
    let pi = 0;
    for (const id in counts) { const r = recipeById(id); d.text(this.fBody, `${r?.name} \u00d7${counts[id]}`, x + PANEL.pad + (pi % 2) * 250, y + 220 + Math.floor(pi / 2) * 26, { color: C.timberDark }); pi++; }
    const ch = CHAPTERS[this.st.chapter - 1];
    const inner = w - PANEL.pad * 2;
    // these ran to the very edge with no padding, and never wrapped
    let cy = y + h - 76;
    cy = this.panelText(d, this.fSmall, `Chapter ${ch.roman}: ${ch.title} — ${ch.goal}`, x + PANEL.pad, cy, inner, 18, C.gold);
    this.panelText(d, this.fSmall, this.st.goalProgress(), x + PANEL.pad, cy + 2, inner, 18, C.timber);
    d.text(this.fSmall, 'Tab / I / X to close', d.w / 2, y + h - 18, { align: 'center', color: C.timber });
  }

  private drawDialog(d: Draw): void {
    this.scrim(d, 0.45);
    const dl = this.dialog!;
    const bodyLines: string[] = [];
    const dlgInner = 560 - PANEL.pad * 2;
    for (const b of dl.body) { if (!b) continue; for (const ln of this.wrapPx(this.fBody, b, dlgInner)) bodyLines.push(ln); }
    const h = 120 + bodyLines.length * 24 + dl.options.length * 34;
    const w = 560, x = d.w / 2 - w / 2, y = d.h - h - 40;
    this.parchment(d, x, y, w, h);
    d.text(this.fH1, dl.title, x + PANEL.pad, y + 18, { color: C.ink });
    let ly = y + 62;
    for (const ln of bodyLines) { d.text(this.fBody, ln, x + PANEL.pad, ly, { color: C.timberDark }); ly += 24; }
    ly += 6;
    for (let i = 0; i < dl.options.length; i++) {
      const on = i === dl.sel;
      if (on) d.rect(x + PANEL.pad - 12, ly - 3, w - (PANEL.pad - 12) * 2, 30, C.gold, 0.2);
      const label = this.wrapPx(this.fBody, `${on ? '>' : ' '} ${dl.options[i].label}`, w - PANEL.pad * 2)[0];
      d.text(this.fBody, label, x + PANEL.pad, ly, { color: on ? C.ink : C.timber });
      ly += 34;
    }
  }

  private drawCodex(d: Draw): void {
    this.scrim(d);
    const w = 640, h = 480, x = d.w / 2 - w / 2, y = d.h / 2 - h / 2;
    this.parchment(d, x, y, w, h);
    d.text(this.fH1, "Joseph's Story", d.w / 2, y + 18, { align: 'center', color: C.ink });
    const list = this.foundEntries();
    if (list.length === 0) {
      d.text(this.fBody, 'You have found no scrolls yet.', d.w / 2, y + h / 2 - 30, { align: 'center', color: C.timber });
      d.text(this.fSmall, 'Explore Nazareth — scrolls of Joseph\'s story are hidden about.', d.w / 2, y + h / 2, { align: 'center', color: C.timber });
      d.text(this.fSmall, 'Press C or X to close', d.w / 2, y + h - 22, { align: 'center', color: C.timber });
      return;
    }
    if (!this.codexReading) {
      d.text(this.fSmall, `${list.length} of ${CODEX.length} scrolls found`, d.w / 2, y + 52, { align: 'center', color: C.timber });
      for (let i = 0; i < list.length; i++) {
        const on = i === this.codexSel % list.length; const ry = y + 84 + i * 30;
        if (on) d.rect(x + 16, ry - 3, w - 32, 28, C.gold, 0.2);
        d.text(this.fBody, list[i].title, x + 30, ry, { color: on ? C.ink : C.timberDark });
        d.text(this.fSmall, list[i].category, x + w - 30, ry + 2, { align: 'right', color: C.timber });
      }
      d.text(this.fSmall, 'Up/Down choose · X read · C close', d.w / 2, y + h - 22, { align: 'center', color: C.timber });
    } else {
      const e = list[this.codexSel % list.length];
      d.text(this.fH2, e.title, d.w / 2, y + 56, { align: 'center', color: C.ink });
      d.text(this.fSmall, e.category, d.w / 2, y + 88, { align: 'center', color: C.gold });
      const lines = this.wrapPx(this.fBody, e.body, 640 - PANEL.pad * 2);
      for (let i = 0; i < lines.length; i++) d.text(this.fBody, lines[i], x + PANEL.pad, y + 120 + i * 26, { color: C.timberDark });
      d.text(this.fSmall, 'X / C to return', d.w / 2, y + h - 22, { align: 'center', color: C.timber });
    }
  }

  private drawChop(d: Draw): void {
    this.scrim(d, 0.4);
    const w = 460, x = d.w / 2 - w / 2, y = d.h / 2 - 60;
    this.parchment(d, x, y, w, 130);
    d.text(this.fH2, this.chop.done ? this.chop.msg : 'Time your cut', d.w / 2, y + 16, { align: 'center', color: this.chop.msg.includes('CLEAN') ? '#3c7a3c' : C.ink });
    const bx = x + 40, bw = w - 80, by = y + 62;
    d.rect(bx, by, bw, 20, '#00000030');
    d.rect(bx + bw * 0.28, by, bw * 0.44, 20, C.gold, 0.35);   // zone
    d.rect(bx + bw * 0.5 - 2, by - 4, 4, 28, C.leafDark);       // centre
    if (!this.chop.done) d.rect(bx + bw * this.chop.pos - 3, by - 6, 6, 32, '#c33');
    d.text(this.fSmall, this.chop.done ? '' : 'Press Space / X in the golden zone', d.w / 2, y + 96, { align: 'center', color: C.timber });
  }

  private drawMinigame(d: Draw): void {
    this.scrim(d, 0.45);
    const m = this.mg;
    const w = 500, h = 220, x = d.w / 2 - w / 2, y = d.h / 2 - h / 2;
    this.parchment(d, x, y, w, h);
    const steps = ['Measure', 'Cut', 'Join'];
    d.text(this.fH1, m.recipe?.name ?? '', d.w / 2, y + 16, { align: 'center', color: C.ink });
    if (m.resultT > 0) {
      d.text(this.fH2, `${QUALITY_NAMES[m.quality]}!`, d.w / 2, y + h / 2 - 20, { align: 'center', color: m.quality === 2 ? C.gold : '#3c7a3c' });
      d.text(this.fSmall, m.quality === 2 ? 'A masterwork — worthy of the finest home.' : 'Honest, useful work.', d.w / 2, y + h / 2 + 12, { align: 'center', color: C.timber });
      return;
    }
    d.text(this.fH2, `Step ${m.step + 1} of 3 — ${steps[m.step]}`, d.w / 2, y + 54, { align: 'center', color: C.timberDark });
    const half = [0.24, 0.17, 0.12][m.step];
    const bx = x + 40, bw = w - 80, by = y + 108;
    d.rect(bx, by, bw, 22, '#00000030');
    d.rect(bx + bw * (0.5 - half), by, bw * half * 2, 22, C.gold, 0.35);
    d.rect(bx + bw * 0.5 - 2, by - 4, 4, 30, C.leafDark);
    d.rect(bx + bw * m.pos - 3, by - 6, 6, 34, '#c33');
    // pips for finished steps
    for (let i = 0; i < 3; i++) d.circle(x + w / 2 - 20 + i * 20, y + h - 34, 6, i < m.step ? C.gold : '#00000022');
    d.text(this.fSmall, 'Press Space / X in the golden zone', d.w / 2, y + h - 16, { align: 'center', color: C.timber });
  }

  private drawPrayer(d: Draw): void {
    this.scrim(d, 0.62);
    const lines = this.wrapPx(this.fH2, this.prayerText, Math.min(760, d.w - 120));
    d.circle(d.w / 2, d.h / 2 - 84, 6, C.gold, 0.8);
    d.ring(d.w / 2, d.h / 2 - 84, 12, 2, C.gold, 0.5);
    for (let i = 0; i < lines.length; i++) d.text(this.fH2, lines[i], d.w / 2, d.h / 2 - 40 + i * 34, { align: 'center', color: C.parchment });
    d.text(this.fSmall, 'Joseph pauses in prayer. Press X to rise.', d.w / 2, d.h / 2 + 90, { align: 'center', color: C.goldLight, alpha: 0.6 + Math.sin(this.t * 3) * 0.3 });
  }

  private drawChapter(d: Draw): void {
    this.scrim(d, 0.55);
    const ch = CHAPTERS[this.chapterShown - 1];
    d.text(this.fBig, `Chapter ${ch.roman}`, d.w / 2, d.h / 2 - 70, { align: 'center', color: C.goldLight });
    d.text(this.fH1, ch.title, d.w / 2, d.h / 2 - 14, { align: 'center', color: C.white });
    d.text(this.fBody, ch.goal, d.w / 2, d.h / 2 + 34, { align: 'center', color: C.parchment });
    d.text(this.fSmall, 'Press X to continue', d.w / 2, d.h / 2 + 84, { align: 'center', color: C.goldLight, alpha: 0.6 + Math.sin(this.t * 3) * 0.3 });
  }

  private drawEnding(d: Draw): void {
    // a gentle scripted close; after a few beats, go to the devotional ending scene
    this.scrim(d, Math.min(0.85, this.endT * 0.3));
    const cx = d.w / 2;
    d.text(this.fH1, 'The well canopy is finished at sunset.', cx, d.h * 0.34, { align: 'center', color: C.goldLight });
    if (this.endT > 2.5) d.text(this.fBody, 'The neighbors Joseph helped gather to give thanks.', cx, d.h * 0.44, { align: 'center', color: C.parchment });
    if (this.endT > 4.5) d.text(this.fBody, 'He receives their praise, and quietly returns home.', cx, d.h * 0.52, { align: 'center', color: C.parchment });
    if (this.endT > 6.5) d.text(this.fSmall, 'Press X', cx, d.h * 0.62, { align: 'center', color: C.goldLight, alpha: 0.6 + Math.sin(this.t * 3) * 0.3 });
    if (this.endT > 6.5 && this.confirmPressed()) { this.st.freePlay = true; this.gotoGameOver({ win: true }); }
  }
}
